// src/lib/tracking/useTest.ts
// ════════════════════════════════════════════════════════════════════════════
// THE ONE HOOK FOR AB TESTS
// ════════════════════════════════════════════════════════════════════════════

import { browser } from '$app/environment';
import type { Test } from './ab-tests';
import { setCustomDimension, trackEvent, trackGoal } from './tracker.svelte';
import { dev } from '$app/environment';

const PREFIX = 'ab:';
const cache = new Map<string, string>();

// ════════════════════════════════════════════════════════════════════════════
// INTERNALS
// ════════════════════════════════════════════════════════════════════════════

function assign<V extends string>(variants: readonly V[], weights?: readonly number[]): V {
  const r = Math.random();
  if (!weights || weights.length !== variants.length) {
    return variants[Math.floor(r * variants.length)];
  }
  const total = weights.reduce((a, b) => a + b, 0);
  let cum = 0;
  for (let i = 0; i < variants.length; i++) {
    cum += weights[i] / total;
    if (r < cum) return variants[i];
  }
  return variants[variants.length - 1];
}

function getFromUrl<V extends string>(testId: string, variants: readonly V[]): V | null {
  if (!browser) return null;
  const params = new URLSearchParams(location.search);
  // Support both ?ab:cv-upload=b and ?variant=b
  const v = params.get(`ab:${testId}`) ?? params.get('variant');
  return v && (variants as readonly string[]).includes(v) ? (v as V) : null;
}

// ════════════════════════════════════════════════════════════════════════════
// PUBLIC API
// ════════════════════════════════════════════════════════════════════════════

/**
 * Get variant for a test (can be used outside components)
 */
export function getVariant<V extends string>(test: Test<V>): V {
  if (!browser) return test.variants[0];

  const key = `${PREFIX}${test.id}`;

  // 1. Memory cache
  const cached = cache.get(test.id);
  if (cached && (test.variants as readonly string[]).includes(cached)) {
    return cached as V;
  }

  // 2. URL override
  const fromUrl = getFromUrl(test.id, test.variants);
  if (fromUrl) {
    cache.set(test.id, fromUrl);
    localStorage.setItem(key, fromUrl);
    return fromUrl;
  }

  // 3. localStorage
  const stored = localStorage.getItem(key);
  if (stored && (test.variants as readonly string[]).includes(stored)) {
    cache.set(test.id, stored);
    return stored as V;
  }

  // 4. New assignment
  const variant = assign(test.variants, test.weights);
  cache.set(test.id, variant);
  localStorage.setItem(key, variant);

  if (dev) {
    console.log(`[AB] ${test.id}: assigned "${variant}"`);
  }

  return variant;
}

/**
 * Test instance returned by useTest
 */
export interface TestInstance<V extends string> {
  /** Test ID */
  readonly id: string;
  /** Assigned variant */
  readonly variant: V;
  /** Check if current variant matches */
  readonly is: (v: V) => boolean;
  /** Track a funnel event */
  readonly event: (action: string, value?: number) => void;
  /** Track goal completion */
  readonly goal: (goalId: number, revenue?: number) => void;
}

/**
 * Hook to use an AB test in a Svelte component
 * 
 * @example
 * ```svelte
 * <script lang="ts">
 *   import { cvUploadTest } from '$lib/tracking/ab-tests';
 *   import { useTest } from '$lib/tracking/useTest';
 * 
 *   const test = useTest(cvUploadTest);
 *   
 *   function handleClick() {
 *     test.event('button-clicked');
 *     if (test.is('a')) {
 *       // variant A behavior
 *     }
 *   }
 * </script>
 * ```
 */
export function useTest<V extends string>(test: Test<V>): TestInstance<V> {
  const variant = getVariant(test);
  const category = `ab:${test.id}`;

  // Set dimension once per session
  let dimSet = false;
  const ensureDim = () => {
    if (!dimSet && test.matomoDimension) {
      setCustomDimension(test.matomoDimension, `${test.id}:${variant}`);
      dimSet = true;
    }
  };

  return {
    id: test.id,
    variant,
    is: (v) => variant === v,
    event: (action, value) => {
      ensureDim();
      trackEvent(category, action, variant, value);
      if (dev) {
        console.log(`[AB] ${test.id}:${variant} → ${action}`);
      }
    },
    goal: (goalId, revenue) => {
      ensureDim();
      trackGoal(goalId, revenue);
      if (dev) {
        console.log(`[AB] ${test.id}:${variant} → goal:${goalId}`);
      }
    },
  };
}

/**
 * Clear all AB test assignments (for debugging)
 */
export function clearAllTests(): void {
  if (!browser) return;
  cache.clear();
  Object.keys(localStorage)
    .filter((k) => k.startsWith(PREFIX))
    .forEach((k) => localStorage.removeItem(k));
  if (dev) {
    console.log('[AB] All tests cleared');
  }
}